import { forwardRef, type PropsWithChildren } from "react";
import { toast } from "sonner";

import CloseIcon from "$/lib/icons/CloseIcon";
import DisconnectedWarningIcon from "$/lib/icons/DisconnectedWarningIcon";
import { InfoIcon } from "$/lib/icons/InfoIcon";
import SuccessCircleIcon from "$/lib/icons/SuccessCircleIcon";
import { SuccessIcon } from "$/lib/icons/SuccessIcon";
import { WarningIcon } from "$/lib/icons/WarningIcon";
import { cn } from "$/lib/utils/functions/misc.functions";

import type { CustomToastProps, ToastType } from "./CustomToast.types";

const Icons: {
  [key in ToastType]: JSX.Element;
} = {
  info: <InfoIcon />,
  success: <SuccessCircleIcon className="size-6 text-[#00D9A5]" />,
  warning: <WarningIcon />,
  error: <DisconnectedWarningIcon className="size-6" />,
};

const PROGRESS_COLOR: Record<ToastType, string> = {
  info: "#696680",
  success: "#7AE7D0",
  warning: "#FF9500",
  error: "#FFCCA9",
};

const CustomToast = forwardRef<
  HTMLDivElement,
  PropsWithChildren<CustomToastProps>
>(({ message, type }, forwardedRef) => {
  return (
    <div
      ref={forwardedRef}
      className={cn(
        "relative flex max-w-[calc(100vw-2rem)] flex-row items-center gap-4 rounded-lg px-3 py-3 text-white screen850:max-w-96",
        {
          "bg-[#FFFFFF] shadow-[3px_3px_10px_rgba(0,0,0,0.25)]":
            type === "success",
          "bg-neutral-600": type === "info",
          "bg-warning": type === "warning",
          "bg-[#FFFCFA] shadow-[4px_4px_10px_rgba(0,0,0,0.25)]":
            type === "error",
        },
      )}
    >
      <div className="flex flex-row items-center justify-center gap-2.5">
        {Icons[type]}
        <p className="w-72 font-semibold text-textColor">{message}</p>
      </div>

      <button
        className="rounded-circle px-0.5 py-2 duration-150 hover:bg-primary/10"
        onClick={() => toast.dismiss()}
      >
        <CloseIcon className="size-2" color="black" />
      </button>
    </div>
  );
});

CustomToast.displayName = "CustomToast";

export default CustomToast;
